import React, { useMemo, useState } from 'react'
import type { AssignableVessel, VesselAccessMode } from '../core/_requests'

type Props = {
  accessMode: VesselAccessMode
  onAccessModeChange: (mode: VesselAccessMode) => void
  selectedVesselIds: Set<number>
  onSelectedVesselIdsChange: (next: Set<number>) => void
  vessels: AssignableVessel[]
  loading: boolean
  vesselsLoading: boolean
  saving: boolean
  error?: string | null
  disabled?: boolean
}

const vesselLabel = (vessel: AssignableVessel): string => {
  const name = vessel.name?.trim() || `Vessel #${vessel.id}`
  const imo = vessel.imoNumber?.trim()
  return imo ? `${name} — ${imo}` : `${name} — #${vessel.id}`
}

export const OperatorVesselAccessSection: React.FC<Props> = ({
  accessMode,
  onAccessModeChange,
  selectedVesselIds,
  onSelectedVesselIdsChange,
  vessels,
  loading,
  vesselsLoading,
  saving,
  error,
  disabled = false,
}) => {
  const [search, setSearch] = useState('')

  const filteredVessels = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return vessels
    return vessels.filter((vessel) => {
      const name = vessel.name?.toLowerCase() || ''
      const imo = vessel.imoNumber?.toLowerCase() || ''
      return name.includes(needle) || imo.includes(needle) || String(vessel.id).includes(needle)
    })
  }, [search, vessels])

  const selectedCount = selectedVesselIds.size
  const allIds = useMemo(() => vessels.map((vessel) => vessel.id), [vessels])
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedVesselIds.has(id))
  const someSelected = selectedCount > 0 && !allSelected

  const updateSelected = (id: number, checked: boolean) => {
    const next = new Set(selectedVesselIds)
    if (checked) {
      next.add(id)
    } else {
      next.delete(id)
    }
    onSelectedVesselIdsChange(next)
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      onSelectedVesselIdsChange(new Set())
      return
    }
    onSelectedVesselIdsChange(new Set(allIds))
  }

  const clearSelection = () => {
    onSelectedVesselIdsChange(new Set())
  }

  const anyDisabled = disabled || saving

  return (
    <section className='border rounded-3 p-4 bg-light-subtle'>
      <div className='d-flex flex-column gap-1 mb-4'>
        <h6 className='mb-0 fw-bold'>Vessel Access</h6>
        <div className='text-muted small'>
          Control which vessels this user can access within their assigned company scope.
        </div>
      </div>

      {loading ? (
        <div className='d-flex align-items-center gap-2 text-muted py-2'>
          <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
          Loading vessel access...
        </div>
      ) : null}

      {error ? (
        <div className='alert alert-danger py-2 px-3 mb-3'>{error}</div>
      ) : null}

      <div className='d-grid gap-3 mb-4'>
        <label className='form-check form-check-custom form-check-solid align-items-start'>
          <input
            className='form-check-input mt-1'
            type='radio'
            name='vesselAccessMode'
            checked={accessMode === 'ALL'}
            onChange={() => onAccessModeChange('ALL')}
            disabled={anyDisabled}
          />
          <span className='form-check-label ms-3'>
            <span className='fw-semibold d-block'>All vessels</span>
            <span className='text-muted small'>Access all vessels within the user's assigned company scope.</span>
          </span>
        </label>

        <label className='form-check form-check-custom form-check-solid align-items-start'>
          <input
            className='form-check-input mt-1'
            type='radio'
            name='vesselAccessMode'
            checked={accessMode === 'ASSIGNED'}
            onChange={() => onAccessModeChange('ASSIGNED')}
            disabled={anyDisabled}
          />
          <span className='form-check-label ms-3'>
            <span className='fw-semibold d-block'>Selected vessels</span>
            <span className='text-muted small'>Access only specifically selected vessels.</span>
          </span>
        </label>

        <label className='form-check form-check-custom form-check-solid align-items-start'>
          <input
            className='form-check-input mt-1'
            type='radio'
            name='vesselAccessMode'
            checked={accessMode === 'NONE'}
            onChange={() => onAccessModeChange('NONE')}
            disabled={anyDisabled}
          />
          <span className='form-check-label ms-3'>
            <span className='fw-semibold d-block'>No vessel access</span>
            <span className='text-muted small'>No vessel-level data access.</span>
          </span>
        </label>
      </div>

      {accessMode === 'ASSIGNED' ? (
        <div className='border rounded-3 p-3 bg-white'>
          <div className='d-flex flex-wrap align-items-center gap-2 mb-3'>
            <div className='fw-semibold'>Assignable vessels</div>
            <span className='badge badge-light-primary'>
              {selectedCount} selected
            </span>
            <div className='ms-auto d-flex flex-wrap gap-2'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={toggleSelectAll}
                disabled={anyDisabled || allIds.length === 0}
              >
                {allSelected ? 'Unselect all' : 'Select all'}
              </button>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={clearSelection}
                disabled={anyDisabled || selectedCount === 0}
              >
                Clear
              </button>
            </div>
          </div>

          <div className='mb-3'>
            <input
              className='form-control form-control-sm'
              placeholder='Search vessel name or IMO...'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              disabled={anyDisabled || vesselsLoading}
            />
          </div>

          {vesselsLoading ? (
            <div className='d-flex align-items-center gap-2 text-muted py-3'>
              <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
              Loading assignable vessels...
            </div>
          ) : null}

          {!vesselsLoading && vessels.length === 0 && !error ? (
            <div className='alert alert-light border mb-0'>
              No vessels are available within this user's company scope.
            </div>
          ) : null}

          {!vesselsLoading && vessels.length > 0 ? (
            <div className='border rounded-3 overflow-auto' style={{ maxHeight: 280 }}>
              {filteredVessels.length === 0 ? (
                <div className='p-3 text-muted'>No vessels match your search.</div>
              ) : (
                filteredVessels.map((vessel, index) => {
                  const checked = selectedVesselIds.has(vessel.id)
                  return (
                    <label
                      key={vessel.id}
                      className={`d-flex align-items-start gap-3 px-3 py-3 border-bottom ${
                        checked ? 'bg-light-primary' : 'bg-white'
                      }`}
                      style={{ cursor: anyDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      <input
                        className='form-check-input mt-1'
                        type='checkbox'
                        checked={checked}
                        onChange={(event) => updateSelected(vessel.id, event.target.checked)}
                        disabled={anyDisabled}
                        aria-label={`Select vessel ${vesselLabel(vessel)}`}
                      />
                      <div className='flex-grow-1'>
                        <div className='fw-semibold'>{vesselLabel(vessel)}</div>
                        <div className='text-muted small'>
                          ID {vessel.id}
                          {vessel.companyAdminId != null ? ` · Company ${vessel.companyAdminId}` : ''}
                          {vessel.companyGroupAdminId != null ? ` · Group ${vessel.companyGroupAdminId}` : ''}
                        </div>
                      </div>
                      <span className='text-muted small'>{index + 1}</span>
                    </label>
                  )
                })
              )}
            </div>
          ) : null}

          {someSelected ? (
            <div className='mt-3 d-flex flex-wrap gap-2'>
              {Array.from(selectedVesselIds).map((id) => {
                const vessel = vessels.find((item) => item.id === id)
                const label = vessel ? vesselLabel(vessel) : `Vessel #${id}`
                return (
                  <span key={id} className='badge badge-light-primary text-wrap'>
                    {label}
                  </span>
                )
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

export default OperatorVesselAccessSection
